// src/store/userSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getUserProfile, getUserProfileByToken, login, getCaptcha, verifyCaptcha, verifyPasswordLogin, verifyGoogleLogin } from '../api/user';
import { saveUserDataSecurely, verifyStorageDomain } from '../utils/storageUtils';

// 从localStorage获取初始用户状态的辅助函数
const getInitialState = () => {
  try {
    // 只检查用户信息，如果有完整的用户信息就认为已登录
    const savedUser = localStorage.getItem('userInfo');
    const accessToken = localStorage.getItem('access_token');
    
    console.log('初始化用户状态检查:', {
      hasSavedUser: !!savedUser,
      hasAccessToken: !!accessToken,
      domain: window.location.origin
    });
    
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        // 确保用户对象有必要的字段，并且有有效的token
        if (user && (user.email || user.uid)) {
          console.log('✅ 从localStorage恢复用户登录状态');
          return {
            user: user,
            isLoggedIn: true,
            hasValidToken: !!accessToken, // 标记是否有有效token
            captcha: '',
            loading: false,
            error: '',
          };
        }
      } catch (e) {
        console.log('Failed to parse saved user from localStorage:', e);
        // 清除损坏的数据
        localStorage.removeItem('userInfo');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
    }
  } catch (error) {
    console.log('Error reading from localStorage:', error);
  }
  
  // 默认未登录状态
  console.log('❌ 未找到有效的用户登录状态');
  return {
    user: null,
    isLoggedIn: false,
    hasValidToken: false,
    captcha: '',
    loading: false,
    error: '',
  };
};

// 异步 Thunk：获取验证码
export const fetchCaptcha = createAsyncThunk('user/fetchCaptcha', async (email, { rejectWithValue }) => {
  console.log('fetchCaptcha called with email:', email); // 追踪调用
  try {
    const response = await getCaptcha(email);
    console.log('fetchCaptcha response:', response);
    
    // 根据你的API响应格式判断成功与否
    if (response && response.code === 1) {
    console.log('fetchCaptcha succeeded:', response);
      return {
        message: response.message,
        email: email,
      };
    } else {
      // code不为1，视为失败
      console.error('fetchCaptcha failed with response:', response);
      return rejectWithValue(response?.message || '发送验证码失败');
    }
  } catch (err) {
    console.error('fetchCaptcha failed:', err.message);
    return rejectWithValue(err.message);
  }
});

// 异步 Thunk：校验验证码
export const verifyCaptchaThunk = createAsyncThunk('user/verifyCaptcha', async ({ phoneNumbers, smsCode }, { rejectWithValue }) => {
  console.log('verifyCaptchaThunk called with phoneNumbers:', phoneNumbers, 'smsCode:', smsCode);
  try {
    const response = await verifyCaptcha({ phoneNumbers, smsCode });
    console.log('verifyCaptchaThunk response:', response);
    
    // 根据后端返回的code判断成功情况
    if (response && (response.code === 100 || response.code === 101 || response.code === 102)) {
      console.log('verifyCaptchaThunk succeeded:', response);
      
      // 后端直接返回用户信息，无需额外调用fetchUserProfile
      if (response.data && response.data.length > 0) {
        const userData = response.data[0]; // 取第一个用户数据
        
        // 将用户信息保存到localStorage
        localStorage.setItem('uid', userData.uid);
        localStorage.setItem('userInfo', JSON.stringify(userData));
        
        // 保存 tokens
        if (response.tokens) {
          if (response.tokens.access_token) {
            localStorage.setItem('access_token', response.tokens.access_token);
          }
          if (response.tokens.refresh_token) {
            localStorage.setItem('refresh_token', response.tokens.refresh_token);
          }
        }
        
        return {
          ...userData,
          message: response.message,
          userType: response.user, // 'existing' 或 'new'
          tokens: response.tokens // 将tokens也返回给redux
        };
      } else {
        // 特殊账号处理 (code: 102)
        const userName = phoneNumbers.split('@')[0];
        const basicUserData = {
          uid: phoneNumbers,
          email: phoneNumbers,
          username: userName
        };
        
        localStorage.setItem('uid', phoneNumbers);
        localStorage.setItem('userInfo', JSON.stringify(basicUserData));
        
        // 保存 tokens（如果有的话）
        if (response.tokens) {
          if (response.tokens.access_token) {
            localStorage.setItem('access_token', response.tokens.access_token);
          }
          if (response.tokens.refresh_token) {
            localStorage.setItem('refresh_token', response.tokens.refresh_token);
          }
        }
        
        return {
          ...basicUserData,
          message: response.message,
          userType: 'special',
          tokens: response.tokens // 将tokens也返回给redux
        };
      }
    } else {
      // 验证失败
      console.error('verifyCaptchaThunk failed with response:', response);
      return rejectWithValue(response?.message || '验证码验证失败');
    }
  } catch (err) {
    console.error('verifyCaptchaThunk failed:', err.message);
    return rejectWithValue(err.message);
  }
});

// 异步 Thunk：账号密码登录
export const verifyPasswordLoginThunk = createAsyncThunk('user/verifyPasswordLogin', async ({ phoneNumbers, password }, { rejectWithValue }) => {
  console.log('verifyPasswordLoginThunk called with phoneNumbers:', phoneNumbers);
  try {
    const response = await verifyPasswordLogin({ phoneNumbers, password });
    console.log('verifyPasswordLoginThunk response:', response);
    
    // 根据后端返回的code判断成功情况
    if (response && (response.code === 100 || response.code === 101 || response.code === 102)) {
      console.log('verifyPasswordLoginThunk succeeded:', response);
      
      // 后端直接返回用户信息，无需额外调用fetchUserProfile
      if (response.data && response.data.length > 0) {
        const userData = response.data[0]; // 取第一个用户数据
        
        // 将用户信息保存到localStorage
        localStorage.setItem('uid', userData.uid);
        localStorage.setItem('userInfo', JSON.stringify(userData));
        
        // 保存 tokens
        if (response.tokens) {
          if (response.tokens.access_token) {
            localStorage.setItem('access_token', response.tokens.access_token);
          }
          if (response.tokens.refresh_token) {
            localStorage.setItem('refresh_token', response.tokens.refresh_token);
          }
        }
        
        return {
          ...userData,
          message: response.message,
          userType: response.user, // 'existing' 或 'new'
          tokens: response.tokens // 将tokens也返回给redux
        };
      } else {
        // 特殊账号处理 (code: 102)
        const userName = phoneNumbers.split('@')[0];
        const basicUserData = {
          uid: phoneNumbers,
          email: phoneNumbers,
          username: userName
        };
        
        localStorage.setItem('uid', phoneNumbers);
        localStorage.setItem('userInfo', JSON.stringify(basicUserData));
        
        // 保存 tokens（如果有的话）
        if (response.tokens) {
          if (response.tokens.access_token) {
            localStorage.setItem('access_token', response.tokens.access_token);
          }
          if (response.tokens.refresh_token) {
            localStorage.setItem('refresh_token', response.tokens.refresh_token);
          }
        }
        
        return {
          ...basicUserData,
          message: response.message,
          userType: 'special',
          tokens: response.tokens // 将tokens也返回给redux
        };
      }
    } else {
      // 验证失败
      console.error('verifyPasswordLoginThunk failed with response:', response);
      return rejectWithValue(response?.message || '密码验证失败');
    }
  } catch (err) {
    console.error('verifyPasswordLoginThunk failed:', err.message);
    return rejectWithValue(err.message);
  }
});

// 异步 Thunk：获取用户信息
export const fetchUserProfile = createAsyncThunk('user/fetchUserProfile', async (_, { rejectWithValue }) => {
  console.log('fetchUserProfile called'); // 追踪调用
  const uid = localStorage.getItem('uid');
  if (!uid) {
    console.log('No uid found in localStorage');
    return rejectWithValue('No uid found in localStorage, user not logged in');
  }
  try {
    const response = await getUserProfile({ uid });
    console.log('fetchUserProfile succeeded:', response.data[0]);
    return response.data[0];
  } catch (err) {
    console.error('fetchUserProfile failed:', err.message);
    return rejectWithValue(err.message);
  }
});

// 新增 Thunk: 通过 token 登录
export const loginByToken = createAsyncThunk('user/loginByToken', async (url_login_token, { rejectWithValue }) => {
  console.log('loginByToken called with token:', url_login_token); // 追踪调用
  try {
    const response = await getUserProfileByToken({ url_login_token });
    console.log('loginByToken response:', response);
    
    // 根据实际 API 响应格式调整条件判断
    if (response && response.success === true) {
      // 从响应中获取用户数据
      const userData = response.data;
      
      // 保存用户 ID 到本地存储
      if (userData && userData.uid) {
        localStorage.setItem('uid', userData.uid);
        console.log('loginByToken succeeded:', userData);
        return userData;
      } else {
        console.error('loginByToken failed: missing uid in response data');
        return rejectWithValue('Missing user data in response');
      }
    } else {
      console.error('loginByToken failed with response:', response);
      return rejectWithValue(response?.msg || 'Token login failed');
    }
  } catch (err) {
    console.error('loginByToken failed with error:', err.message);
    return rejectWithValue(err.message);
  }
});

// 异步 Thunk：登录
export const loginUser = createAsyncThunk('user/login', async ({ uid }, { rejectWithValue }) => {
  console.log('loginUser called with uid:', uid); // 追踪调用
  try {
    const response = await login({ uid });
    localStorage.setItem('token', response.token);
    localStorage.setItem('uid', uid);
    console.log('loginUser succeeded:', response.data[0]);
    return response.data[0];
  } catch (err) {
    console.error('loginUser failed:', err.message);
    return rejectWithValue(err.message);
  }
});

// 新增 Thunk: 邮箱自动登录（用于支付成功后）
export const autoLoginWithEmail = createAsyncThunk('user/autoLoginWithEmail', async (email, { rejectWithValue }) => {
  console.log('autoLoginWithEmail called with email:', email); // 追踪调用
  
  if (!email) {
    return rejectWithValue('Email is required for auto login');
  }
  
  try {
    // 将邮箱保存到localStorage作为uid
    localStorage.setItem('uid', email);
    
    // 调用getUserProfile获取用户信息
    const response = await getUserProfile({ uid: email });
    console.log('autoLoginWithEmail getUserProfile response:', response);
    
    if (response && response.data && response.data.length > 0) {
      const userData = response.data[0];
      console.log('autoLoginWithEmail got user data:', userData);

      console.log('autoLoginWithEmail succeeded:', userData);
      return userData;
    } else {
      console.log('autoLoginWithEmail: getUserProfile returned empty data, creating basic user info');
      // 对于新用户，创建基本的用户信息
      const basicUserInfo = {
        email: email,
        uid: email,
        name: email.split('@')[0] // 使用邮箱前缀作为用户名
      };
      
      return basicUserInfo;
    }
    
  } catch (err) {
    console.error('autoLoginWithEmail failed:', err.message);
    // 即使API调用失败，我们仍然可以创建基本的用户信息
    console.log('autoLoginWithEmail: API failed, creating basic user info for:', email);
    const basicUserInfo = {
      email: email,
      uid: email,
      name: email.split('@')[0]
    };
    
    return basicUserInfo;
  }
});

// 异步 Thunk：Google登录验证
export const verifyGoogleLoginThunk = createAsyncThunk('user/verifyGoogleLogin', async (params, { rejectWithValue }) => {
  console.log('verifyGoogleLoginThunk called with params:', params);
  try {
    const response = await verifyGoogleLogin(params);
    console.log('verifyGoogleLoginThunk response:', response);
    
    // 检查响应格式，适配后端返回的数据结构
    if (response && (response.success === true || response.code === 100 || response.code === 101)) {
      // 处理 data 数组格式的响应
      let userData;
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        userData = response.data[0]; // 取第一个用户数据
      } else {
        userData = response.user || response.data;
      }
      
      if (userData) {
        // 使用安全的存储工具保存用户数据
        saveUserDataSecurely(userData, response.tokens);
        
        console.log('verifyGoogleLoginThunk succeeded:', userData);
        return {
          ...userData,
          authProvider: 'google'
        };
      } else {
        console.error('verifyGoogleLoginThunk failed: missing user data');
        return rejectWithValue('Google登录失败：缺少用户数据');
      }
    } else {
      console.error('verifyGoogleLoginThunk failed with response:', response);
      return rejectWithValue(response?.message || response?.msg || 'Google登录验证失败');
    }
  } catch (err) {
    console.error('verifyGoogleLoginThunk failed:', err.message);
    
    // 特殊处理：检查是否是401错误但实际包含成功数据
    if (err.response && err.response.status === 401 && err.response.data) {
      const responseData = err.response.data;
      if (responseData.success === true || responseData.user || responseData.data || responseData.code === 100) {
        console.log('401错误但包含成功数据，尝试处理:', responseData);
        // 按成功路径处理
        let userData;
        if (responseData.data && Array.isArray(responseData.data) && responseData.data.length > 0) {
          userData = responseData.data[0]; // 取第一个用户数据
        } else {
          userData = responseData.user || responseData.data;
        }
        
        if (userData) {
          // 使用安全的存储工具保存用户数据（401错误处理）
          saveUserDataSecurely(userData, responseData.tokens);
          
          return {
            ...userData,
            authProvider: 'google'
          };
        }
      }
    }
    
    return rejectWithValue(err.message || 'Google登录失败');
  }
});

// 异步 Thunk：退出登录（简化版）
export const logoutUser = createAsyncThunk('user/logout', async (_, { rejectWithValue }) => {
  console.log('logoutUser called'); // 追踪调用
  try {
    // 清除所有用户相关的localStorage数据
    localStorage.removeItem('token');    // 清除 token
    localStorage.removeItem('access_token'); // 清除 access_token
    localStorage.removeItem('refresh_token'); // 清除 refresh_token
    localStorage.removeItem('uid');      // 清除 uid  
    localStorage.removeItem('userInfo'); // 清除用户信息
    console.log('logoutUser succeeded - all localStorage data cleared');
    return null; // 无需后端交互，直接返回成功
  } catch (err) {
    console.error('logoutUser failed:', err.message);
    return rejectWithValue(err.message);
  }
});

const userSlice = createSlice({
  name: 'user',
  initialState: getInitialState(), // 使用动态初始状态
  reducers: {
    clearError: (state) => {
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCaptcha.pending, (state) => {
        state.loading = true;
        state.error = '';
        console.log('fetchCaptcha pending'); // 追踪状态
      })
      .addCase(fetchCaptcha.fulfilled, (state, action) => {
        state.captcha = action.payload;
        state.loading = false;
        console.log('fetchCaptcha fulfilled:', action.payload);
      })
      .addCase(fetchCaptcha.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch captcha';
        state.loading = false;
        console.log('fetchCaptcha rejected:', action.payload);
      })
      .addCase(verifyCaptchaThunk.pending, (state) => {
        state.loading = true;
        state.error = '';
        console.log('verifyCaptchaThunk pending'); // 追踪状态
      })
      .addCase(verifyCaptchaThunk.fulfilled, (state, action) => {
        // 验证码验证成功，设置为已登录状态
        state.user = action.payload; // 包含email和message信息
        state.isLoggedIn = true;
        state.hasValidToken = !!(action.payload.tokens && action.payload.tokens.access_token);
        state.loading = false;
        // 保存用户信息到localStorage（不包含tokens）
        const userInfoToSave = { ...action.payload };
        delete userInfoToSave.tokens; // 不在userInfo中保存tokens
        localStorage.setItem('userInfo', JSON.stringify(userInfoToSave));
        console.log('verifyCaptchaThunk fulfilled:', action.payload);
      })
      .addCase(verifyCaptchaThunk.rejected, (state, action) => {
        state.error = action.payload || 'Captcha verification failed';
        state.loading = false;
        console.log('verifyCaptchaThunk rejected:', action.payload);
      })
      .addCase(verifyPasswordLoginThunk.pending, (state) => {
        state.loading = true;
        state.error = '';
        console.log('verifyPasswordLoginThunk pending'); // 追踪状态
      })
      .addCase(verifyPasswordLoginThunk.fulfilled, (state, action) => {
        // 密码验证成功，设置为已登录状态
        state.user = action.payload; // 包含email和message信息
        state.isLoggedIn = true;
        state.hasValidToken = !!(action.payload.tokens && action.payload.tokens.access_token);
        state.loading = false;
        // 保存用户信息到localStorage（不包含tokens）
        const userInfoToSave = { ...action.payload };
        delete userInfoToSave.tokens; // 不在userInfo中保存tokens
        localStorage.setItem('userInfo', JSON.stringify(userInfoToSave));
        console.log('verifyPasswordLoginThunk fulfilled:', action.payload);
      })
      .addCase(verifyPasswordLoginThunk.rejected, (state, action) => {
        state.error = action.payload || 'Password verification failed';
        state.loading = false;
        console.log('verifyPasswordLoginThunk rejected:', action.payload);
      })
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = '';
        console.log('fetchUserProfile pending'); // 追踪状态
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isLoggedIn = true;
        state.loading = false;
        // 保存用户信息到localStorage
        localStorage.setItem('userInfo', JSON.stringify(action.payload));
        console.log('fetchUserProfile fulfilled:', state.user);
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch user profile';
        state.isLoggedIn = false;
        state.loading = false;
        console.log('fetchUserProfile rejected:', action.payload);
      })
      .addCase(loginByToken.pending, (state) => {
        state.loading = true;
        state.error = '';
        console.log('loginByToken pending'); // 追踪状态
      })
      .addCase(loginByToken.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isLoggedIn = true;
        state.loading = false;
        // 保存用户信息到localStorage
        localStorage.setItem('userInfo', JSON.stringify(action.payload));
        console.log('loginByToken fulfilled:', state.user);
      })
      .addCase(loginByToken.rejected, (state, action) => {
        state.error = action.payload || 'Token login failed';
        state.loading = false;
        console.log('loginByToken rejected:', action.payload);
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = '';
        console.log('loginUser pending'); // 追踪状态
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isLoggedIn = true;
        state.loading = false;
        // 保存用户信息到localStorage
        localStorage.setItem('userInfo', JSON.stringify(action.payload));
        console.log('loginUser fulfilled:', state.user);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.error = action.payload || 'Login failed';
        state.loading = false;
        console.log('loginUser rejected:', action.payload);
      })
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = '';
        console.log('logoutUser pending'); // 追踪状态
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isLoggedIn = false;
        state.hasValidToken = false;
        state.loading = false;
        // 清除localStorage中的用户信息
        localStorage.removeItem('userInfo');
        console.log('logoutUser fulfilled');
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.error = action.payload || 'Logout failed';
        state.loading = false;
        console.log('logoutUser rejected:', action.payload);
      })
      .addCase(autoLoginWithEmail.pending, (state) => {
        state.loading = true;
        state.error = '';
        console.log('autoLoginWithEmail pending'); // 追踪状态
      })
      .addCase(autoLoginWithEmail.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isLoggedIn = true;
        state.loading = false;
        // 保存用户信息到localStorage
        localStorage.setItem('userInfo', JSON.stringify(action.payload));
        console.log('autoLoginWithEmail fulfilled:', state.user);
      })
      .addCase(autoLoginWithEmail.rejected, (state, action) => {
        state.error = action.payload || 'Auto login with email failed';
        state.loading = false;
        console.log('autoLoginWithEmail rejected:', action.payload);
      })
      .addCase(verifyGoogleLoginThunk.pending, (state) => {
        state.loading = true;
        state.error = '';
        console.log('verifyGoogleLoginThunk pending'); // 追踪状态
      })
      .addCase(verifyGoogleLoginThunk.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isLoggedIn = true;
        state.hasValidToken = true;
        state.loading = false;
        // 保存用户信息到localStorage
        localStorage.setItem('userInfo', JSON.stringify(action.payload));
        console.log('verifyGoogleLoginThunk fulfilled:', action.payload);
      })
      .addCase(verifyGoogleLoginThunk.rejected, (state, action) => {
        state.error = action.payload || 'Google login failed';
        state.loading = false;
        console.log('verifyGoogleLoginThunk rejected:', action.payload);
      });
  },
});

export const { clearError } = userSlice.actions;
export default userSlice.reducer;